import { getNotificationColorDependingOnType } from 'pages/notifications/helpers';
import type { Notification } from 'pages/notifications/types';

import { parseTextWithLinks } from 'components/NotificationBanner/helper/parseTextWithLinks';

import { InfoOutlined } from '@mui/icons-material';
import { Alert, AlertTitle, Typography } from '@mui/material';
import type { AlertColor } from '@mui/material/Alert/Alert';

import { getLinkColorBySeverity } from './helper/getLinkColorBySeverity';
import { StarterKit } from '@tiptap/starter-kit';

import DOMPurify from 'dompurify';
import { RichTextReadOnly } from 'mui-tiptap';

// Tiptap 3 StarterKit ships link, underline, listKeymap and trailingNode; v2 had none of them, so only
// link (default options, same as the standalone Link used before) stays on to keep rendering unchanged.
const extensions = [StarterKit.configure({ underline: false, listKeymap: false, trailingNode: false })];

const NotificationBanner = ({ notification, onClose }: { notification: Notification; onClose: () => void }) => {
    const linkColor = getLinkColorBySeverity(notification.level);

    const linkStyles = {
        color: linkColor,
        textDecoration: 'underline',
        wordBreak: 'break-word',
    };

    const cleanHtml = DOMPurify.sanitize(notification.text);
    return (
        <Alert
            icon={<InfoOutlined fontSize="inherit" />}
            severity={getNotificationColorDependingOnType(notification.level) as AlertColor}
            variant="filled"
            onClose={onClose}
            sx={{
                py: 0,
                px: '8px',
                display: 'flex',
                alignItems: 'flex-start',
                marginTop: '60px',
                '& .MuiAlert-icon': {
                    mr: '8px',
                    mt: '18px',
                },
                '& .MuiAlert-message': {
                    paddingTop: '20px',
                    paddingBottom: '20px',
                },
            }}
            aria-haspopup="true"
        >
            <AlertTitle
                sx={{
                    m: 0,
                    display: 'flex',
                    alignItems: 'flex-start',
                    flexDirection: 'column',
                    '& a': linkStyles,
                }}
            >
                {parseTextWithLinks(notification.title)}
            </AlertTitle>
            <Typography
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    flexWrap: 'wrap',
                    '& a': linkStyles,
                }}
                variant="body2"
            >
                <RichTextReadOnly content={cleanHtml} extensions={extensions}></RichTextReadOnly>
            </Typography>
        </Alert>
    );
};

export { NotificationBanner };
